import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Bell, Mail, Clock, AlertTriangle, 
  RefreshCw, CheckCircle, XCircle, Activity, Send,
  Smartphone, FileText, Calendar, TrendingUp, Power, Pause
} from 'lucide-react';

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color = 'blue', trend = null }) => {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  
  const iconColors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
  };
  
  return (
    <div className={`rounded-xl p-4 border ${colorStyles[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-sm opacity-75">{label}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend !== null && (
        <div className="mt-2 text-xs flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          {trend > 0 ? '+' : ''}{trend}% rispetto a ieri
        </div>
      )}
    </div>
  );
};

// Provider Status Badge
const ProviderStatus = ({ name, status }) => {
  const statusStyles = {
    healthy: 'bg-green-100 text-green-700',
    degraded: 'bg-yellow-100 text-yellow-700',
    unavailable: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-700',
  };
  
  const statusIcons = {
    healthy: CheckCircle,
    degraded: AlertTriangle,
    unavailable: XCircle,
    unknown: Clock,
  };
  
  const Icon = statusIcons[status] || Clock;
  
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-gray-700 font-medium">{name}</span>
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusStyles[status] || statusStyles.unknown}`}>
        <Icon className="w-3 h-3" />
        {status === 'healthy' ? 'Attivo' : status === 'degraded' ? 'Degradato' : status === 'unavailable' ? 'Non disponibile' : 'Sconosciuto'}
      </span>
    </div>
  );
};

// Quick Action Button
const QuickAction = ({ icon: Icon, label, to, color = 'gray' }) => {
  const colorStyles = {
    gray: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    blue: 'bg-blue-100 hover:bg-blue-200 text-blue-700',
    green: 'bg-green-100 hover:bg-green-200 text-green-700',
    amber: 'bg-amber-100 hover:bg-amber-200 text-amber-700',
  };
  
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${colorStyles[color]}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
        <Icon className="w-5 h-5" />
      </div>
      <span className="font-medium">{label}</span>
    </Link>
  );
};

// Scheduler Status Banner
const SchedulerBanner = ({ status }) => {
  if (!status) return null;
  
  const isSimulation = status.simulation_mode;
  const isRunning = status.is_running;
  const isAutoSend = status.auto_send_enabled;
  
  return (
    <div className={`rounded-xl p-4 border ${
      isSimulation 
        ? 'bg-amber-50 border-amber-200' 
        : 'bg-green-50 border-green-200'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isRunning ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            <Power className={`w-5 h-5 ${isRunning ? 'text-green-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Scheduler Engine</span>
              {isRunning ? (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Attivo</span>
              ) : (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Inattivo</span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-0.5">
              {isSimulation ? (
                <>Modalità <strong className="text-amber-700">SIMULAZIONE</strong> - i messaggi non vengono inviati automaticamente</>
              ) : (
                <>Modalità <strong className="text-green-700">PRODUZIONE</strong> - invio automatico {isAutoSend ? 'attivo' : 'disattivo'}</>
              )}
            </p>
          </div>
        </div>
        <Link
          to="/admin/automation/simulation"
          className="px-4 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
        >
          Gestisci →
        </Link>
      </div>
      
      {/* Safe Events */}
      {status.safe_events_enabled && (
        <div className="mt-3 pt-3 border-t border-amber-200/50">
          <p className="text-xs text-gray-600 mb-2">Eventi automatici abilitati (Phase 5.2A):</p>
          <div className="flex flex-wrap gap-1">
            {status.safe_events_enabled.map(event => (
              <span key={event} className="px-2 py-0.5 bg-white/60 text-gray-700 text-xs rounded border border-gray-200/50">
                {event.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function AutomationDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/communication/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Errore nel caricamento statistiche');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/communication/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchedulerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchStats();
      fetchSchedulerStatus();
      const interval = setInterval(() => {
        fetchStats();
        fetchSchedulerStatus();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  if (loading) {
    return (
      <AdminLayout title="Automation Dashboard">
        <div className="p-6 animate-pulse max-w-7xl mx-auto">
          <div className="h-32 bg-gray-200 rounded-xl mb-4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Automation Dashboard">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Communication Center</h1>
            <p className="text-gray-500 mt-1">Monitora e gestisci le comunicazioni automatiche</p>
          </div>
          <button
            onClick={() => { fetchStats(); fetchSchedulerStatus(); }}
            className="p-2.5 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors shadow-sm"
            title="Aggiorna"
            data-testid="refresh-dashboard"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Scheduler Status Banner */}
        <SchedulerBanner status={schedulerStatus} />

        {/* Stats Grid - Sent Today */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Invii di Oggi</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="WhatsApp"
              value={stats?.whatsapp_sent_today || 0}
              icon={MessageSquare}
              color="green"
            />
            <StatCard
              label="Push"
              value={stats?.push_sent_today || 0}
              icon={Bell}
              color="purple"
            />
            <StatCard
              label="Email"
              value={stats?.email_sent_today || 0}
              icon={Mail}
              color="blue"
            />
            <StatCard
              label="Totale"
              value={(stats?.whatsapp_sent_today || 0) + (stats?.push_sent_today || 0) + (stats?.email_sent_today || 0)}
              icon={Send}
              color="amber"
            />
          </div>
        </div>

        {/* Stats Grid - Queue Status */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Stato Coda</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Programmati"
              value={schedulerStatus?.queue_stats?.queued || stats?.scheduled || 0}
              icon={Clock}
              color="blue"
            />
            <StatCard
              label="In Elaborazione"
              value={schedulerStatus?.queue_stats?.processing || stats?.in_queue || 0}
              icon={Activity}
              color="amber"
            />
            <StatCard
              label="Falliti"
              value={schedulerStatus?.queue_stats?.failed || stats?.failed || 0}
              icon={XCircle}
              color="red"
            />
            <StatCard
              label="Da Ritentare"
              value={schedulerStatus?.queue_stats?.retry_pending || stats?.retry_pending || 0}
              icon={RefreshCw}
              color="orange"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Provider Status */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Stato Provider</h2>
              <Link to="/admin/automation/providers" className="text-sm text-blue-600 hover:text-blue-800">
                Gestisci →
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {stats?.provider_status && Object.keys(stats.provider_status).length > 0 ? (
                Object.entries(stats.provider_status).map(([name, status]) => (
                  <ProviderStatus key={name} name={name} status={status} />
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">Nessun provider configurato</p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Azioni Rapide</h2>
            <div className="grid grid-cols-1 gap-3">
              <QuickAction 
                icon={Smartphone} 
                label="Configura Provider" 
                to="/admin/automation/providers"
                color="blue"
              />
              <QuickAction 
                icon={FileText} 
                label="Gestisci Template" 
                to="/admin/automation/templates"
                color="green"
              />
              <QuickAction 
                icon={Calendar} 
                label="Simulazione Invii" 
                to="/admin/automation/simulation"
                color="amber"
              />
              <QuickAction 
                icon={AlertTriangle} 
                label="Messaggi Falliti" 
                to="/admin/automation/logs?status=failed"
                color="gray"
              />
            </div>
          </div>
        </div>

        {/* Timestamp */}
        {stats?.generated_at && (
          <p className="text-xs text-gray-400 text-center">
            Ultimo aggiornamento: {new Date(stats.generated_at).toLocaleString('it-IT')}
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
